// src/pages/prayers/components/PrayerCardsGrid.tsx
import React from 'react';
import {
  Heart,
  MessageCircle,
  Share,
  MoreVertical,
  Bookmark,
  CheckCircle,
  Flag,
  Edit,
  Trash2,
  UserX,
} from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useMobileDetection } from '@/contexts/MobileDetectionContext';
import { usePrayerChoice } from '@/contexts/PrayerChoiceContext';
import { getAuthorId, isOwnedBy } from '@/utils/ownership';
import type { Prayer } from '@/types/prayer';
import { usePrayerActions } from '@/hooks/usePrayerActions';

interface PrayerCardsGridProps {
  prayers: Prayer[];
  onPrayerCardClick: (prayer: Prayer) => void;
  onComment: (prayer: Prayer) => void;
  onShare: (prayer: Prayer) => void;
  onPrayerStatus: (id: string) => void;
  onEdit: (id: string) => void;
  onReport: (id: string) => void;
  onDelete: (id: string) => void;
  onBlockUser?: (userId: string, username?: string) => void;
  onPullToRefresh?: () => Promise<void>;
  isRefreshing?: boolean;
  currentUserId?: string;
}

export default function PrayerCardsGrid({
  prayers,
  onPrayerCardClick,
  onComment,
  onShare,
  onPrayerStatus,
  onEdit,
  onReport,
  onDelete,
  onBlockUser,
  onPullToRefresh,
  isRefreshing = false,
  currentUserId,
}: PrayerCardsGridProps) {
  const { isMobile } = useMobileDetection();
  const { openChoiceDialog } = usePrayerChoice();
  const { user } = useAuth();

  const { toggleFavorite, toggleBookmark } = usePrayerActions();

  const handlePrayerIconClick = (prayer: Prayer) => {
    openChoiceDialog(
      { id: prayer.id, title: prayer.title, content: prayer.content },
      'prayer_card_grid'
    );
  };

  return (
    <div
      className="flex-1 px-4 pt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-20"
      onTouchStart={(e) => {
        if (!isMobile || !onPullToRefresh) return;
        const startY = e.touches[0].clientY;
        const scrollTop = window.pageYOffset;
        if (scrollTop === 0) {
          const handleTouchMove = (moveEvent: TouchEvent) => {
            const currentY = moveEvent.touches[0].clientY;
            const deltaY = currentY - startY;
            if (deltaY > 100) {
              onPullToRefresh();
              document.removeEventListener('touchmove', handleTouchMove);
            }
          };
          document.addEventListener('touchmove', handleTouchMove, { once: true });
        }
      }}
    >
      {prayers.map((prayer) => {
        // ✅ Safe author fallback
        const authorName =
          prayer.author ||
          (prayer as any)?.profiles?.full_name ||
          (prayer as any)?.profiles?.username ||
          'Anonymous';

        const initials = authorName
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase();

        return (
          <Card
            key={prayer.id}
            variant="glass-gold"
            className="cursor-pointer relative animate-fade-in transition-all hover:shadow-2xl"
            onClick={() => onPrayerCardClick(prayer)}
          >
            <CardContent className="p-4 flex flex-col">
              {/* Category + Group */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex gap-1 flex-wrap w-[60%]">
                  {prayer.category && (
                    <span className="px-1.5 py-0.5 text-xs rounded font-medium bg-gray-200 dark:bg-gray-700 text-black dark:text-white">
                      {prayer.category}
                    </span>
                  )}
                  {prayer.group && (
                    <span className="px-1.5 py-0.5 text-xs bg-purple-200 dark:bg-purple-800 rounded font-medium text-black dark:text-white">
                      {prayer.group}
                    </span>
                  )}
                </div>
              </div>

              {/* Title + Content */}
              <h3 className="text-lg font-semibold mb-2 text-black dark:text-white">
                {prayer.title || 'Untitled Prayer'}
              </h3>
              <p className="text-base leading-relaxed mb-3 line-clamp-2 text-black dark:text-white">
                {prayer.content}
              </p>

              {/* Answered testimony */}
              {prayer.status === 'answered' &&
                prayer.testimony &&
                prayer.isTestimonyPublic && (
                  <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-400">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm font-medium text-green-800 dark:text-green-200">
                        Prayer Answered!
                      </span>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300 italic">
                      "{prayer.testimony}"
                    </p>
                  </div>
                )}

              {/* Author + Date */}
              <div className="flex justify-between items-center text-xs text-black dark:text-white">
                <div className="flex items-center gap-2">
                  <Avatar className="w-5 h-5">
                    <AvatarImage
                      src={
                        prayer.authorAvatarStoragePath ||
                        `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(
                          authorName
                        )}`
                      }
                      alt={authorName}
                    />
                    <AvatarFallback className="text-[9px]">{initials}</AvatarFallback>
                  </Avatar>
                  <span>By {authorName}</span>
                </div>
                <div className="flex items-center gap-2">
                  {prayer.status === 'answered' && (
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  )}
                  <span>{new Date(prayer.date).toLocaleDateString()}</span>
                </div>
              </div>

              {/* 🔥 Horizontal Action Icons (desktop) */}
              <div className="absolute top-2 right-2 flex gap-2 z-[15] pointer-events-auto">
                {/* Pray */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrayerIconClick(prayer);
                  }}
                  className="transition-all text-gold hover:text-gold/80 flex items-center justify-center"
                >
                  <span className="text-lg">🙏</span>
                </button>

                {/* Favorite */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(prayer.id);
                  }}
                  className={`transition-all flex items-center justify-center ${
                    prayer.isFavorite
                      ? 'text-red-500'
                      : 'text-muted-foreground hover:text-red-500'
                  }`}
                >
                  <Heart
                    className="w-4 h-4"
                    fill={prayer.isFavorite ? 'currentColor' : 'none'}
                  />
                </button>

                {/* Comment */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onComment(prayer);
                  }}
                  className="transition-all text-foreground hover:text-primary flex items-center justify-center"
                >
                  <MessageCircle className="w-4 h-4" />
                </button>

                {/* Share */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare(prayer);
                  }}
                  className="transition-all text-foreground hover:text-primary flex items-center justify-center"
                >
                  <Share className="w-4 h-4" />
                </button>

                {/* More Options */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="transition-all text-foreground hover:text-primary flex items-center justify-center"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="end"
                    className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-black dark:text-white z-50"
                  >
                    {/* Bookmark */}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleBookmark(prayer.id);
                      }}
                    >
                      <Bookmark
                        className="w-3 h-3 mr-2"
                        fill={prayer.isBookmarked ? 'currentColor' : 'none'}
                        style={{ color: prayer.isBookmarked ? '#9333EA' : undefined }}
                      />
                      {prayer.isBookmarked ? 'Remove Bookmark' : 'Bookmark'}
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {/* Status */}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onPrayerStatus(prayer.id);
                      }}
                    >
                      <CheckCircle className="w-3 h-3 mr-2" />
                      Prayer Status
                    </DropdownMenuItem>

                    {currentUserId && isOwnedBy(prayer, currentUserId) && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(prayer.id);
                        }}
                      >
                        <Edit className="w-3 h-3 mr-2" />
                        Edit Prayer
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />

                    {/* Block / Report / Delete */}
                    {onBlockUser && user && !isOwnedBy(prayer, user.id) && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          const authorId = getAuthorId(prayer);
                          if (authorId) onBlockUser(authorId, authorName);
                        }}
                      >
                        <UserX className="w-3 h-3 mr-2 text-orange-400" />
                        Block User
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onReport(prayer.id);
                      }}
                    >
                      <Flag className="w-3 h-3 mr-2 text-red-400" />
                      Report
                    </DropdownMenuItem>

                    {currentUserId && isOwnedBy(prayer, currentUserId) && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(prayer.id);
                        }}
                      >
                        <Trash2 className="w-3 h-3 mr-2 text-red-400" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
